/**
 * Telemetry Module
 * Application Insights integration for tracking retrieval API calls and errors.
 * Logs flow to Log Analytics workspace via the App Insights resource.
 *
 * Set `appInsightsConnectionString` in config.json (injected per environment by CI/CD)
 * to enable. Each environment (dev/uat/prod) uses its own App Insights resource.
 */

import { ApplicationInsights, IEventTelemetry, IExceptionTelemetry } from '@microsoft/applicationinsights-web';
import { getConfig } from './config';

let appInsights: ApplicationInsights | null = null;

/**
 * Initialise Application Insights once.
 * Called from main.ts after config is loaded.
 */
export function initTelemetry(): void {
  const connectionString = getConfig().appInsightsConnectionString;

  if (!connectionString) {
    console.log('[Telemetry] appInsightsConnectionString not set in config.json — App Insights disabled.');
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: false,   // SPA — we track manually
      disableAjaxTracking: false,        // still track general XHR/fetch
      disableFetchTracking: false,
      enableRequestHeaderTracking: false,
      enableResponseHeaderTracking: false,
    },
  });

  appInsights.loadAppInsights();
  appInsights.trackPageView({ name: 'RFP Assistant' });
  console.log('[Telemetry] Application Insights initialised.');
}

/**
 * Get the App Insights client (may be null if not configured).
 */
function ai(): ApplicationInsights | null {
  return appInsights;
}

// ─────────────────────────────────────────────────────────────────────────────
// Retrieval API telemetry
// ─────────────────────────────────────────────────────────────────────────────

export interface RetrievalResult {
  name: string;
  url: string;
  score: number;
  aboveThreshold: boolean;
  contentLength: number;
}

export interface RetrievalCallProps {
  query: string;
  requestBody: Record<string, unknown>;
  httpStatus: number;
  durationMs: number;
  totalItems: number;
  aboveThresholdItems: number;
  threshold: number;
  top: number;
  cacheHit: boolean;
  results: RetrievalResult[];
}

/**
 * Track a completed retrieval API call (success or no-results).
 * Shows in Log Analytics as:
 *   customEvents | where name == "RetrievalAPI"
 */
export function trackRetrieval(props: RetrievalCallProps): void {
  const client = ai();
  if (!client) return;

  const event: IEventTelemetry = {
    name: 'RetrievalAPI',
    properties: {
      // All values must be strings for customDimensions in Log Analytics
      query:              props.query,
      httpStatus:         String(props.httpStatus),
      durationMs:         String(Math.round(props.durationMs)),
      totalItems:         String(props.totalItems),
      aboveThresholdItems: String(props.aboveThresholdItems),
      threshold:          String(props.threshold),
      top:                String(props.top),
      cacheHit:           String(props.cacheHit),
      hadResults:         String(props.aboveThresholdItems > 0),
      filterExpression:   String(props.requestBody.filterExpression ?? ''),
      topResult_name:     props.results[0]?.name ?? '',
      topResult_url:      props.results[0]?.url ?? '',
      topResult_score:    String(props.results[0]?.score ?? 0),
      results:            JSON.stringify(props.results),
    },
    // Numeric fields go in measurements so they appear as customMeasurements
    // and support avg/sum aggregations in KQL
    measurements: {
      durationMs:         props.durationMs,
      httpStatus:         props.httpStatus,
      totalItems:         props.totalItems,
      aboveThresholdItems: props.aboveThresholdItems,
      topResult_score:    props.results[0]?.score ?? 0,
    },
  };

  client.trackEvent(event);
  client.flush();
}

/**
 * Track a retrieval API error (HTTP error or network failure).
 * Shows in Log Analytics as:
 *   exceptions | where outerMessage contains "RetrievalAPI"
 */
export function trackRetrievalError(query: string, error: unknown, durationMs: number): void {
  const client = ai();
  if (!client) return;

  const err = error instanceof Error ? error : new Error(String(error));

  const telemetry: IExceptionTelemetry = {
    exception: err,
    properties: {
      source:    'RetrievalAPI',
      query,
      durationMs: String(Math.round(durationMs)),
    },
    measurements: {
      durationMs,
    },
  };

  client.trackException(telemetry);
  client.flush();
}
